import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Spin } from "@/components/ui";

interface Props {
  onConfirm: () => void;
  busy?: boolean;
  label: string;
  confirmLabel?: string;
  danger?: boolean;
  className?: string;
}

/** Two-click confirm: first click arms, second click (or Confirm) fires. */
export function ConfirmButton({
  onConfirm,
  busy,
  label,
  confirmLabel,
  danger = true,
  className = "",
}: Props) {
  const { t } = useTranslation();
  const [armed, setArmed] = useState(false);

  const base =
    "rounded-md px-2.5 py-1 text-xs font-medium transition disabled:opacity-50 ";
  const tone = danger
    ? "bg-[#FEECEC] text-[#B42318] hover:bg-[#FDDCDC]"
    : "border border-olive-600 text-sand-muted hover:text-sand";

  if (!armed) {
    return (
      <button
        type="button"
        className={base + tone + " " + className}
        onClick={() => setArmed(true)}
        disabled={busy}
        aria-busy={busy || undefined}
      >
        {busy ? <Spin className="mr-1.5" /> : null}
        {label}
      </button>
    );
  }

  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        className={base + "bg-red-500/90 text-white hover:bg-red-500"}
        onClick={() => {
          onConfirm();
          setArmed(false);
        }}
        disabled={busy}
        aria-busy={busy || undefined}
      >
        {busy ? <Spin className="mr-1.5" /> : null}
        {confirmLabel ?? t("common.confirm")}
      </button>
      <button
        type="button"
        className={base + "text-sand-muted hover:text-sand"}
        onClick={() => setArmed(false)}
        disabled={busy}
      >
        {t("common.cancel")}
      </button>
    </span>
  );
}
